import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Table, TableHead,
  TableRow, TableCell, TableBody, Chip, Alert, CircularProgress,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getDowntimes, recordDowntime, endDowntime, type ProductionOrderListItem, type DowntimeCause } from '../../api/productieOrdine'
import { errorMessage } from '../../api/client'

const CAUSE_LABEL: Record<DowntimeCause, string> = {
  DefectiuneMecanica: 'Defecțiune mecanică', LipsaMaterial: 'Lipsă material', SchimbMatrita: 'Schimb matriță',
  Pauza: 'Pauză', Curatare: 'Curățare', LipsaUtilitate: 'Lipsă utilitate', Altele: 'Altele',
}

export default function DowntimeDialog({ target, onClose }: { target: ProductionOrderListItem | null; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const [cause, setCause] = useState<DowntimeCause>('DefectiuneMecanica')
  const [notes, setNotes] = useState('')

  const { data } = useQuery({ queryKey: ['productie', 'ordine', target?.id, 'opriri'], queryFn: () => getDowntimes(target!.id), enabled: open })

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['productie', 'ordine', target?.id, 'opriri'] })
  const recordMutation = useMutation({
    mutationFn: () => recordDowntime(target!.id, { startedAt: new Date().toISOString(), cause, notes: notes || null }),
    onSuccess: () => { invalidate(); setNotes('') },
  })
  const endMutation = useMutation({ mutationFn: (downtimeId: string) => endDowntime(target!.id, downtimeId), onSuccess: invalidate })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Opriri — {target?.number}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5}>
            <TextField select size="small" label="Cauză" value={cause} onChange={(e) => setCause(e.target.value as DowntimeCause)} sx={{ width: 200 }}>
              {Object.entries(CAUSE_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Notă" value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ flexGrow: 1 }} />
            <Button variant="outlined" disabled={recordMutation.isPending} onClick={() => recordMutation.mutate()}
              startIcon={recordMutation.isPending ? <CircularProgress size={14} /> : undefined}>
              Înregistrează
            </Button>
          </Stack>
          {recordMutation.isError && <Alert severity="error">{errorMessage(recordMutation.error)}</Alert>}

          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Cauză</TableCell><TableCell>Început</TableCell><TableCell>Sfârșit</TableCell><TableCell>Notă</TableCell><TableCell /></TableRow>
            </TableHead>
            <TableBody>
              {data?.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>{CAUSE_LABEL[d.cause]}</TableCell>
                  <TableCell>{new Date(d.startedAt).toLocaleString('ro-RO')}</TableCell>
                  <TableCell>
                    {d.endedAt ? new Date(d.endedAt).toLocaleString('ro-RO') : <Chip size="small" color="warning" label="în curs" />}
                  </TableCell>
                  <TableCell>{d.notes ?? '—'}</TableCell>
                  <TableCell>
                    {!d.endedAt && (
                      <Button size="small" onClick={() => endMutation.mutate(d.id)} disabled={endMutation.isPending}>Încheie</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
    </Dialog>
  )
}
